import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import useAssistantStore from "@/store/assistantStore";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Plus, Search, X } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  ClaudeIcon,
  CohereIcon,
  DeepSeekIcon,
  GeminiIcon,
  GrokIcon,
  GroqIcon,
  MistralIcon,
  OpenAIIcon,
  OpenRouterIcon,
  PerplexityIcon,
  QwenIcon,
} from "./icons";

// Determine provider from model name
const determineProvider = (model) => {
  if (!model) return "openai";

  // Check for OpenRouter models which use a slash format
  if (model.includes("/")) {
    const prefix = model.split("/")[0].toLowerCase();
    if (
      prefix === "cognitivecomputations" ||
      prefix === "nousresearch" ||
      prefix === "teknium" ||
      prefix === "migtissera"
    ) {
      return "openrouter";
    }
  }

  // Check if model name includes specific provider names
  if (model.includes("deepseek")) return "deepseek";
  if (model.includes("gemini")) return "gemini";
  if (model.includes("claude")) return "anthropic";
  if (model.includes("qwen")) return "qwen";
  if (model === "mistral-large-2402") return "groq";
  if (model.includes("mistral") && !model.includes("mixtral")) return "mistral";
  if (model.includes("grok")) return "xai";
  if (model.includes("command")) return "cohere";
  if (model.includes("sonar") || model.includes("r1-1776")) return "perplexity";
  if (
    model.includes("gemma") ||
    model.includes("llama") ||
    model.includes("mixtral")
  )
    return "groq";

  return "openai";
};

// Provider icon styling function
const getProviderIcon = (provider, size = 10) => {
  switch (provider) {
    case "deepseek":
      return (
        <DeepSeekIcon
          size={size}
          className="text-[#015482] dark:text-[#015482]"
        />
      );
    case "gemini":
    case "google":
      return (
        <GeminiIcon size={size} className="text-[#1967D2] dark:text-[#1967D2]" />
      );
    case "mistral":
      return (
        <MistralIcon
          size={size}
          className="text-orange-600 dark:text-orange-400"
        />
      );
    case "anthropic":
    case "claude":
      return <ClaudeIcon size={size} className="fill-[#D97757]" />;
    case "groq":
      return (
        <GroqIcon size={size} className="text-green-600 dark:text-green-400" />
      );
    case "xai":
    case "grok":
      return (
        <GrokIcon size={size} className="text-blue-400 dark:text-blue-200" />
      );
    case "cohere":
      return (
        <CohereIcon size={size} className="text-teal-600 dark:text-teal-400" />
      );
    case "perplexity":
      return (
        <PerplexityIcon size={size} className="text-red-600 dark:text-red-400" />
      );
    case "qwen":
      return (
        <QwenIcon size={size} className="text-purple-600 dark:text-purple-400" />
      );
    case "openrouter":
      return (
        <OpenRouterIcon
          size={size}
          className="text-indigo-600 dark:text-indigo-400"
        />
      );
    case "openai":
    default:
      return (
        <OpenAIIcon
          size={size}
          className="fill-black dark:fill-white text-black dark:text-white"
        />
      );
  }
};

export const AssistantSearchDialog = ({
  isOpen,
  onOpenChange,
  onAssistantSelect,
}) => {
  const [search, setSearch] = useState("");
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [storeAssistants, setStoreAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { createAssistantWithConfig, assistants } = useAssistantStore();

  // Fetch assistant store data on mount
  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        const response = await fetch("/data/assistantStore.json");
        if (response.ok) {
          const data = await response.json();
          setStoreAssistants(data);
        }
      } catch (error) {
        console.error("Failed to load assistant store:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssistants();
  }, []);

  // Filter assistants by search
  const filteredAssistants = storeAssistants.filter((assistant) => {
    const searchLower = search.toLowerCase();
    return (
      assistant.name.toLowerCase().includes(searchLower) ||
      assistant.description.toLowerCase().includes(searchLower) ||
      assistant.tags?.some((tag) => tag.toLowerCase().includes(searchLower)) ||
      assistant.category?.toLowerCase().includes(searchLower)
    );
  });

  // Check if assistant is already added
  const isAlreadyAdded = (id) => {
    return assistants?.some(
      (a) =>
        a.id === id ||
        a.name === storeAssistants.find((s) => s.id === id)?.name
    );
  };

  // Handle viewing assistant details
  const handleViewDetails = (assistant) => {
    setSelectedAssistant(assistant);
  };

  // Handle going back to grid
  const handleBack = () => {
    setSelectedAssistant(null);
  };

  // Handle adding assistant
  const handleAddAssistant = async (assistant) => {
    if (isAdding) return;

    setIsAdding(true);
    try {
      const { useAuthStore } = await import("@/store/authStore");
      const personalWorkspaceId = useAuthStore
        .getState()
        .getPersonalWorkspaceId();

      const newAssistant = await createAssistantWithConfig({
        name: assistant.name,
        description: assistant.description,
        model: assistant.model,
        instructions: assistant.instructions,
        tools: assistant.tools || [],
        workspace_id: personalWorkspaceId,
        avatar_url: assistant.avatar_url,
        metadata: {
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_default: "false",
          source_id: assistant.id,
        },
        is_default: false,
      });

      if (newAssistant && onAssistantSelect) {
        onAssistantSelect(newAssistant);
      }
      onOpenChange(false);
      setSelectedAssistant(null);
    } catch (error) {
      console.error("Failed to add assistant:", error);
    } finally {
      setIsAdding(false);
    }
  };

  // Handle closing dialog
  const handleClose = () => {
    setSelectedAssistant(null);
    setSearch("");
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl h-[80vh] p-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border border-gray-200/70 dark:border-zinc-700/70 rounded-2xl overflow-hidden"
        hideCloseButton
      >
        <AnimatePresence mode="wait">
          {selectedAssistant ? (
            // Detail View
            <motion.div
              key="detail"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex flex-col h-full"
            >
              <DialogHeader className="p-6 pb-4 border-b border-border/20">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleBack}
                    className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex-1">
                    <DialogTitle className="text-xl font-semibold">
                      {selectedAssistant.name}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mt-1">
                      Assistant Details
                    </DialogDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClose}
                    className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </DialogHeader>

              <ScrollArea className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-6">
                  {/* Header with avatar */}
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="relative">
                      <Avatar className="w-20 h-20 border-2 border-border/30">
                        <AvatarImage
                          src={selectedAssistant.avatar_url}
                          alt={selectedAssistant.name}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white text-xl font-semibold">
                          {selectedAssistant.name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white dark:bg-zinc-800 flex items-center justify-center border border-border/30">
                        {getProviderIcon(
                          determineProvider(selectedAssistant.model),
                          14
                        )}
                      </div>
                    </div>
                    <div className="flex-1 space-y-2">
                      <h2 className="text-2xl font-bold">
                        {selectedAssistant.name}
                      </h2>
                      <p className="text-muted-foreground">
                        {selectedAssistant.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {selectedAssistant.model}
                        </Badge>
                        {selectedAssistant.category && (
                          <Badge variant="outline" className="text-xs">
                            {selectedAssistant.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {selectedAssistant.tags && selectedAssistant.tags.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedAssistant.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Capabilities */}
                  {selectedAssistant.instructions && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Capabilities</h3>
                      <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground max-h-40 overflow-y-auto">
                        {selectedAssistant.instructions.slice(0, 500)}
                        {selectedAssistant.instructions.length > 500 && "..."}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Action button */}
              <div className="p-6 pt-4 border-t border-border/20">
                <Button
                  onClick={() => handleAddAssistant(selectedAssistant)}
                  disabled={isAdding || isAlreadyAdded(selectedAssistant.id)}
                  className={cn(
                    "w-full rounded-3xl bg-transparent border border-border/30 hover:bg-white/10 dark:hover:bg-white/5",
                    isAdding || isAlreadyAdded(selectedAssistant.id)
                      ? "bg-green-500/20 hover:bg-green-500/20 text-green-600 dark:text-green-400 cursor-not-allowed border-green-500/30"
                      : "text-foreground hover:border-border/50"
                  )}
                  size="lg"
                >
                  {isAdding ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Adding...
                    </>
                  ) : isAlreadyAdded(selectedAssistant.id) ? (
                    "Already Added"
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Assistant
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          ) : (
            // Grid View
            <motion.div
              key="grid"
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col h-[80vh]"
            >
              <DialogHeader className="p-6 pb-4 border-b border-border/20">
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-xl font-semibold">
                      Browse Assistants
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mt-1">
                      Discover and add specialized AI assistants to your
                      collection
                    </DialogDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClose}
                    className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Search input */}
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search assistants..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-white/50 dark:bg-zinc-800/50 border-border/30"
                  />
                </div>
              </DialogHeader>

              <ScrollArea className="flex-1 p-4 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {filteredAssistants.map((assistant) => {
                      const provider = determineProvider(assistant.model);
                      const added = isAlreadyAdded(assistant.id);

                      return (
                        <motion.div
                          key={assistant.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          whileHover={{ y: -2 }}
                          className={cn(
                            "cursor-pointer transition-all duration-200",
                            added && "opacity-60 cursor-not-allowed"
                          )}
                          onClick={() => !added && handleViewDetails(assistant)}
                        >
                          <div className="relative aspect-square rounded-lg overflow-hidden border border-border/30 hover:border-border/60 hover:shadow-md transition-all duration-200">
                            {/* Background */}
                            <div
                              className="absolute inset-0 bg-cover bg-center bg-gradient-to-br from-purple-500 to-indigo-500"
                              style={{
                                backgroundImage: assistant.avatar_url
                                  ? `url(${assistant.avatar_url})`
                                  : undefined,
                              }}
                            />
                            {/* Fallback letter */}
                            {!assistant.avatar_url && (
                              <div className="absolute inset-0 flex items-center justify-center text-white text-2xl font-bold">
                                {assistant.name[0]}
                              </div>
                            )}
                            {/* Provider icon */}
                            <div className="absolute top-2 left-2 z-10">
                              <div className="w-5 h-5 rounded-full bg-white/90 dark:bg-zinc-800/90 flex items-center justify-center border border-border/30">
                                {getProviderIcon(provider, 10)}
                              </div>
                            </div>
                            {/* Added badge */}
                            {added && (
                              <div className="absolute top-2 right-2 z-10">
                                <Badge
                                  variant="secondary"
                                  className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                >
                                  Added
                                </Badge>
                              </div>
                            )}
                          </div>
                          {/* Name */}
                          <div className="mt-2 text-center">
                            <h3 className="font-medium text-xs leading-tight text-foreground/90 line-clamp-2">
                              {assistant.name}
                            </h3>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}

                {/* Empty state */}
                {!isLoading && filteredAssistants.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-muted-foreground">
                      <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">
                        No assistants found
                      </p>
                      <p className="text-sm">Try adjusting your search terms</p>
                    </div>
                  </div>
                )}
              </ScrollArea>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

AssistantSearchDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onAssistantSelect: PropTypes.func,
};

export default AssistantSearchDialog;
